import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, doc, getDoc, collection, addDoc, serverTimestamp } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { notifyCartGifted } from '../utils/notifications';
import { isTokenValid, msUntilExpiry, formatTimeLeft } from '../utils/shareTokens';
import {
  ShoppingBag, User, CreditCard, AlertCircle, Check, Loader2,
  Lock, Sparkles,
} from 'lucide-react';

const PAYSTACK_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

const SharedCartPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);

  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (userData?.name && !payerName) setPayerName(userData.name);
    if (userData?.email && !payerEmail) setPayerEmail(userData.email);
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'shareLinks', token));
        if (cancelled) return;

        if (!snap.exists()) {
          setError('This share link is invalid or has been removed.');
          return;
        }
        const data = snap.data();
        if (!isTokenValid(data)) {
          setError('This share link has expired.');
          return;
        }
        if (!Array.isArray(data.items) || data.items.length === 0) {
          setError('The cart is empty.');
          return;
        }
        setShare(data);
      } catch (err) {
        console.error('Failed to load shared cart:', err);
        if (!cancelled) setError('Something went wrong loading this cart.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#EFA9B6]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={36} className="text-[#EFA9B6]" />
          </div>
          <h1 className="text-xl font-serif font-bold text-gray-900 mb-2">
            Link unavailable
          </h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Go to Skytone
          </button>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-[#FCF8F9] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-500" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            You're amazing 🎁
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {(share.ownerName || 'They').split(' ')[0]} has been notified. Their cart is on the way.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Explore Skytone
          </button>
        </div>
      </div>
    );
  }

  const { items, ownerName, ownerImage, subtotal, itemCount } = share;
  const timeLeft = formatTimeLeft(msUntilExpiry(share));
  const firstName = (ownerName || 'A Skytone user').split(' ')[0];

  const handlePaystackCheckout = () => {
    if (!payerName.trim()) return alert('Please enter your name.');
    if (!payerEmail.trim() || !payerEmail.includes('@')) return alert('Please enter a valid email.');
    if (!PAYSTACK_KEY) {
      alert('Payment is not configured.');
      return;
    }

    setPaying(true);

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_KEY,
      email: payerEmail.trim(),
      amount: Math.round(subtotal * 100),
      currency: 'ZAR',
      metadata: {
        custom_fields: [
          { display_name: 'Gifted to', variable_name: 'gifted_to', value: ownerName },
          { display_name: 'Paid by', variable_name: 'paid_by', value: payerName },
          { display_name: 'Cart owner UID', variable_name: 'cart_owner_uid', value: share.ownerUid },
        ],
      },
      callback: (response) => {
        finalizeOrder(response.reference);
      },
      onClose: () => setPaying(false),
    });

    handler.openIframe();
  };

  const finalizeOrder = async (reference) => {
    try {
      const orderPayload = {
        paidBy: {
          userId: user?.uid || null,
          name: payerName.trim(),
          email: payerEmail.trim(),
          isGuest: !user,
        },
        giftedTo: share.ownerUid,
        giftedToName: ownerName,
        items: items.map((it) => ({
          productId: it.id,
          name: it.name,
          brand: it.brand || null,
          price: Number(it.price || 0),
          quantity: Number(it.quantity || 1),
          image: it.image || null,
          options: it.options || {},
          vendorId: it.professionalId || null,
        })),
        subtotal,
        total: subtotal,
        itemCount,
        status: 'paid',
        paymentMethod: 'paystack',
        paymentReference: reference,
        createdAt: serverTimestamp(),
        source: 'shared_cart',
      };

      const orderRef = await addDoc(collection(db, 'orders'), orderPayload);

      try {
        await notifyCartGifted({
          ownerId: share.ownerUid,
          gifterName: payerName.trim(),
          itemCount,
          orderId: orderRef.id,
        });
      } catch (e) {
        console.warn('Notify failed:', e);
      }

      setPaid(true);
      setPaying(false);
    } catch (err) {
      console.error('Order creation failed:', err);
      alert('Payment received but we could not save your order. Contact support with reference: ' + reference);
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCF8F9] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[#EFA9B6] font-bold tracking-widest text-xs uppercase">
            <Sparkles size={14} />
            Skytone
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#FFF5F6] overflow-hidden flex items-center justify-center shrink-0">
              {ownerImage ? (
                <img src={ownerImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={24} className="text-[#EFA9B6]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                Gift cart
              </p>
              <h1 className="text-xl font-serif font-bold text-gray-900 truncate">
                {firstName}'s cart
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {itemCount} item{itemCount === 1 ? '' : 's'} · {timeLeft}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            What's inside
          </h2>
          <div className="space-y-4">
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-3">
                <div className="w-16 h-16 rounded-2xl bg-[#FCF8F9] flex items-center justify-center shrink-0 overflow-hidden">
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <ShoppingBag size={20} className="text-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-gray-900 truncate">{it.name}</p>
                      {it.brand && (
                        <p className="text-xs text-gray-400 truncate">{it.brand}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Qty {it.quantity}</p>
                    </div>
                    <span className="font-bold text-sm text-gray-900 shrink-0">
                      R{(Number(it.price || 0) * Number(it.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Subtotal</span>
            <span>R{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between pt-3 border-t border-gray-100">
            <span className="font-bold text-gray-900">Total</span>
            <span className="font-bold text-[#EFA9B6] text-lg">
              R{subtotal.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-4">
          <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-4">
            Your details
          </h2>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Your name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            <input
              type="email"
              placeholder="Your email (for receipt)"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-gray-100 focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 outline-none text-sm"
            />
            {!user && (
              <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <Lock size={11} />
                No account needed — we'll email your receipt.
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handlePaystackCheckout}
          disabled={paying || !payerName.trim() || !payerEmail.includes('@')}
          className="w-full bg-[#EFA9B6] text-white py-4 rounded-full font-bold text-sm shadow-lg hover:bg-[#EFA9B6]/90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {paying ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing…
            </>
          ) : (
            <>
              <CreditCard size={18} />
              Check out for {firstName} · R{subtotal.toFixed(2)}
            </>
          )}
        </button>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Secured by Paystack
        </p>
      </div>
    </div>
  );
};

export default SharedCartPage;